import { createSlice } from "@reduxjs/toolkit";
import { authManagementApi } from "Services/AuthManagementService";
import { userCouponApi } from "Services/UserCouponService";
import Cookies from 'universal-cookie';

export const initialState = {
    completedStepIdList: [],
    submissionInProgress: false,
    paymentInProgress: false,
    userInformation: {
        firstName: { value: "", error: "" },
        lastName: { value: "", error: "" },
        phoneNumber: { value: "", error: "" },
        email: { value: "", error: "" },
        password: { value: "", error: "" },
        confirmPassword: { value: "", error: "" },
    },
    organizationInformation: {
        ein: {
            value: "",
            error: "",
        },
        organizationName: {
            value: "",
            error: "",
        },
        organizationAddress: {
            value: "",
            error: "",
        },
        addressLine2: {
            value: "",
            error: "",
        },
        country: {
            value: "",
            error: "",
        },
        city: {
            value: "",
            error: "",
        },
        provinceOrState: {
            value: "",
            error: "",
        },
        foreignStatesList: [],
        cityList: [],
        zipOrPostal: {
            value: "",
            error: "",
        },
        useSameAddress: {
            value: false,
            error: "",
        },
    },
    billingInformation: {
        billingAddress: {
            value: "",
            error: "",
        },
        addressLine2: {
            value: "",
            error: "",
        },
        country: {
            value: "",
            error: "",
        },
        city: {
            value: "",
            error: "",
        },
        provinceOrState: {
            value: "",
            error: "",
        },
        foreignStatesList: [],
        cityList: [],
        zipOrPostal: {
            value: "",
            error: "",
        },
    },
    planSelection: {
        subscriptionPlanId: null,
        coupon: null,
        planList: [],
    },
    paymentType: "",
    userId: null,
    organizationId: null,
    isCouponValidating: false,
};

const registrationV2Slice = createSlice({
    name: "registration-v2",
    initialState,
    reducers: {
        addStepIdToCompletedList: (state, action) => {
            const stepId = action.payload;
            if (!state.completedStepIdList.includes(stepId)) {
                state.completedStepIdList.push(action.payload);
            }
        },
        removeStepIdToCompletedList: (state, action) => {
            state.completedStepIdList = state.completedStepIdList.filter(
                (stepId) => stepId !== action.payload
            );
        },
        setUserInformation: (state, action) => {
            if (state.userInformation[action.payload.field]) {
                if (action.payload.value !== undefined) {
                    state.userInformation[action.payload.field].value =
                        action.payload.value;
                }
                state.userInformation[action.payload.field].error =
                    action.payload.error || "";
            }
        },
        setOrganization: (state, action) => {
            if (state.organizationInformation[action.payload.field]) {
                if (action.payload.value !== undefined) {
                    state.organizationInformation[action.payload.field].value =
                        action.payload.value;
                }
                state.organizationInformation[action.payload.field].error =
                    action.payload.error || "";

                if (state.organizationInformation.useSameAddress.value) {
                    if (action.payload.field === "organizationName") {
                        state.billingInformation.billingAddress.value =
                            action.payload.value;
                    }
                    if (state.billingInformation[action.payload.field]) {
                        state.billingInformation[action.payload.field].value =
                            action.payload.value;
                    }
                }
            }
        },
        setBilling: (state, action) => {
            if (state.billingInformation[action.payload.field]) {
                if (action.payload.value !== undefined) {
                    state.billingInformation[action.payload.field].value =
                        action.payload.value;
                }
                state.billingInformation[action.payload.field].error =
                    action.payload.error || "";
            }
        },
        copyOrganizationToBilling: (state) => {
            state.billingInformation.billingAddress.value =
                state.organizationInformation.organizationAddress.value;
            state.billingInformation.addressLine2.value =
                state.organizationInformation.addressLine2.value;
            state.billingInformation.country.value =
                state.organizationInformation.country.value;
            state.billingInformation.city.value =
                state.organizationInformation.city.value;
            state.billingInformation.provinceOrState.value =
                state.organizationInformation.provinceOrState.value;
            state.billingInformation.zipOrPostal.value =
                state.organizationInformation.zipOrPostal.value;
            state.billingInformation.foreignStatesList =
                state.organizationInformation.foreignStatesList;
        },
        setPlan: (state, action) => {
            state.planSelection[action.payload.field] = action.payload.value;
        },
        setPaymentType: (state, action) => {
            state.paymentType = action.payload;
        },
        setCardDetails: (state, action) => {
            state.cardDetails[action.payload.field] = action.payload.value;
        },
        setErrors: (state, action) => {
            const { group, errors } = action.payload;
            Object.entries(errors).forEach(([key, error]) => {
                state[group][key].error = error[0];
            });
        },
        setSubmissionInProgress: (state, action) => {
            state.submissionInProgress = action.payload;
        },
        setPlanList: (state, action) => {
            state.planSelection.planList = action.payload;
        },
        setCleanupSessionUser: (state, action) => {
            state.userId = null;
            state.organizationId = null;
        },
        setForeignStatesList: (state, action) => {
            state.organizationInformation.foreignStatesList = action.payload;
        },
        setForeignStatesListBilling: (state, action) => {
            state.billingInformation.foreignStatesList = action.payload;
        },
        setCityList: (state, action) => {
            state.organizationInformation.cityList = action.payload;
        },
        setCityListBilling: (state, action) => {
            state.billingInformation.cityList = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle the loading state when user information is being submitted
            .addMatcher(
                authManagementApi.endpoints.signupUser.matchPending,
                (state) => {
                    state.submissionInProgress = true;
                }
            )
            .addMatcher(
                authManagementApi.endpoints.signupUser.matchFulfilled,
                (state, action) => {
                    state.submissionInProgress = false;
                    state.userId = action.payload.data;
                    state.organizationId = null;
                    
                    const cookies = new Cookies();
                    cookies.set('sessionUserId', action.payload.data, { maxAge: 1000000, path: '/', sameSite: true });
                }
            )
            .addMatcher(
                authManagementApi.endpoints.signupUser.matchRejected,
                (state) => {
                    state.submissionInProgress = false;
                }
            )
            // Handle the loading state when organization information is being updated
            .addMatcher(
                authManagementApi.endpoints.signupOrganization.matchPending,
                (state) => {
                    state.submissionInProgress = true;
                }
            )
            .addMatcher(
                authManagementApi.endpoints.signupOrganization.matchFulfilled,
                (state, action) => {
                    state.submissionInProgress = false;
                    state.organizationId = action.payload.data;
                }
            )
            .addMatcher(
                authManagementApi.endpoints.signupOrganization.matchRejected,
                (state) => {
                    state.submissionInProgress = false;
                }
            )
            // Handle the loading state when billing information is being updated
            .addMatcher(
                authManagementApi.endpoints.signupBilling.matchPending,
                (state) => {
                    state.submissionInProgress = true;
                }
            )
            .addMatcher(
                authManagementApi.endpoints.signupBilling.matchFulfilled,
                (state) => {
                    state.submissionInProgress = false;
                }
            )
            .addMatcher(
                authManagementApi.endpoints.signupBilling.matchRejected,
                (state) => {
                    state.submissionInProgress = false;
                }
            )
            .addMatcher(authManagementApi.endpoints.signupSubscription.matchPending, (state) => {
                state.paymentInProgress = true;
            })
            .addMatcher(authManagementApi.endpoints.signupSubscription.matchFulfilled, (state) => {
                state.paymentInProgress = false;
            })
            .addMatcher(authManagementApi.endpoints.signupSubscription.matchRejected, (state) => {
                state.paymentInProgress = false;
            })
            .addMatcher(userCouponApi.endpoints.validateCoupon.matchPending, (state) => {
                state.planSelection.isCouponValidating = true;
            })
            .addMatcher(userCouponApi.endpoints.validateCoupon.matchFulfilled, (state) => {
                state.planSelection.isCouponValidating = false;
            })
            .addMatcher(userCouponApi.endpoints.validateCoupon.matchRejected, (state) => {
                state.planSelection.isCouponValidating = false;
            });
    },
});

const registrationStepperState = (state) => state.registrationStepperState;
export const selectCompletedStepIdList = (state) =>
    registrationStepperState(state).completedStepIdList;
export const selectSubmissionInProgress = (state) =>
    registrationStepperState(state).submissionInProgress;
export const selectPaymentInProgress = (state) =>
    registrationStepperState(state).paymentInProgress;
export const selectUserInformation = (state) =>
    registrationStepperState(state).userInformation;
export const selectOrganizationInformation = (state) =>
    registrationStepperState(state).organizationInformation;
export const selectBillingInformation = (state) =>
    registrationStepperState(state).billingInformation;
export const selectPlan = (state) =>
    registrationStepperState(state).planSelection;
export const selectPaymentType = (state) =>
    registrationStepperState(state).paymentType;
export const selectCardDetails = (state) =>
    registrationStepperState(state).cardDetails;
export const selectUserId = (state) => registrationStepperState(state).userId;
export const selectOrganizationId = (state) => registrationStepperState(state).organizationId;

export const {
    addStepIdToCompletedList,
    removeStepIdToCompletedList,
    setUserInformation,
    setOrganization,
    setBilling,
    copyOrganizationToBilling,
    setPlan,
    setPlanList,
    setPaymentType,
    setCardDetails,
    setErrors,
    setSubmissionInProgress,
    setCleanupSessionUser,
    setForeignStatesList,
    setForeignStatesListBilling,
    setCityList,
    setCityListBilling
} = registrationV2Slice.actions;

export default registrationV2Slice.reducer;
